import { useState } from 'react'
import { Button, Input, Label, Modal } from './ui'

interface CompanyModalProps {
  isOpen: boolean
  onClose: () => void
  onJoinCompany: (companyId: string) => void
  onCreateCompany: (companyName: string) => void
}

export function CompanyModal({ isOpen, onClose, onJoinCompany, onCreateCompany }: CompanyModalProps) {
  const [activeTab, setActiveTab] = useState<'join' | 'create'>('join')
  const [joinCode, setJoinCode] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  const handleJoinCompany = (e: React.FormEvent) => {
    e.preventDefault()
    const newErrors: Record<string, string> = {}

    if (!joinCode.trim()) {
      newErrors.joinCode = 'Company code is required'
    }

    setErrors(newErrors)
    
    if (Object.keys(newErrors).length === 0) {
      onJoinCompany(joinCode)
      setJoinCode('')
      onClose()
    }
  }

  const handleCreateCompany = (e: React.FormEvent) => {
    e.preventDefault()
    const newErrors: Record<string, string> = {}

    if (!companyName.trim()) {
      newErrors.companyName = 'Company name is required'
    }

    setErrors(newErrors)
    
    if (Object.keys(newErrors).length === 0) {
      onCreateCompany(companyName)
      setCompanyName('')
      onClose()
    }
  }

  const resetForm = () => {
    setJoinCode('')
    setCompanyName('')
    setErrors({})
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            Company Management
          </h2>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg mb-6">
          <button
            onClick={() => setActiveTab('join')}
            className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'join'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Join Company
          </button>
          <button
            onClick={() => setActiveTab('create')}
            className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'create'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Create Company
          </button>
        </div>

        {/* Join Company Tab */}
        {activeTab === 'join' && (
          <div>
            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Join an Existing Company</h3>
              <p className="text-sm text-gray-600">
                Enter the company code provided by your administrator to join an existing company.
              </p>
            </div>

            <form onSubmit={handleJoinCompany} className="space-y-4">
              <div>
                <Label htmlFor="joinCode">Company Code *</Label>
                <Input
                  id="joinCode"
                  type="text"
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value)}
                  placeholder="Enter 6-digit company code"
                  className={errors.joinCode ? 'border-red-500' : ''}
                  maxLength={6}
                />
                {errors.joinCode && <p className="mt-1 text-sm text-red-600">{errors.joinCode}</p>}
                <p className="mt-1 text-sm text-gray-500">
                  Ask your team administrator for the company code
                </p>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-4">
                <Button type="button" variant="secondary" onClick={handleClose}>
                  Cancel
                </Button>
                <Button type="submit">
                  Join Company
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Create Company Tab */}
        {activeTab === 'create' && (
          <div>
            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Create a New Company</h3>
              <p className="text-sm text-gray-600">
                Set up a new company workspace. You'll be the administrator and can invite team members.
              </p>
            </div>

            <form onSubmit={handleCreateCompany} className="space-y-4">
              <div>
                <Label htmlFor="companyName">Company Name *</Label>
                <Input
                  id="companyName"
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Enter your company name"
                  className={errors.companyName ? 'border-red-500' : ''}
                />
                {errors.companyName && <p className="mt-1 text-sm text-red-600">{errors.companyName}</p>}
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="text-sm text-blue-700">
                    <p className="font-medium mb-1">What happens next:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Your company will be created with you as the administrator</li>
                      <li>You'll receive a company code to share with team members</li>
                      <li>You can configure locations, schedules, and team settings</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-4">
                <Button type="button" variant="secondary" onClick={handleClose}>
                  Cancel
                </Button>
                <Button type="submit">
                  Create Company
                </Button>
              </div>
            </form>
          </div>
        )}
      </div>
    </Modal>
  )
}